import actions from 'redux/actions'
import { useCallback, useCookies, useEffect } from 'sb-hooks'
import { constants, dataLayer } from 'common/helpers'


type useAnalyticsOptions = {
  redirectLink?: string
  paymentData: PurchaseDataModel.PaymentData
}

// ATTN if you add new cons to here, then add it to useProcessAnalytics too
const cookiesToDelete: string[] = [
  ...Object.values(constants.countdownCookies),
  constants.cookieNames.offer,
  constants.cookieNames.bindedCoupon,
  constants.cookieNames.discountCoupon,
  constants.cookieNames.newPaymentCouponCode,
  constants.cookieNames.utmMediumBeforeSubscription,
  constants.cookieNames.utmSourceBeforeSubscription,
  constants.cookieNames.resubscribeCode,
  constants.cookieNames.logoRedirectUrl,
  constants.cookieNames.isFreeTrialCoupon,
  constants.cookieNames.isFreeTrialOrgFlow,
  constants.cookieNames.paymentCouponFromUser,
  constants.cookieNames.isFacebookFlowEnabled,
  constants.cookieNames.specialFlowName,
  constants.cookieNames.makeupIntroFlow,
  constants.cookieNames.selectedPlan,
  constants.cookieNames.fbPixel,
  constants.cookieNames.shippingFields,
  constants.cookieNames.firstPickProducts,
  constants.cookieNames.authCountdown,
  constants.cookieNames.mainPageThxNotification,
  constants.cookieNames.fbPixelId,
  constants.cookieNames.flowName,
]

const usePurchaseAnalytics = (options: useAnalyticsOptions) => {
  const { redirectLink, paymentData } = options

  const { getCookie, removeCookie } = useCookies()

  const discountCoupon = getCookie<string>(constants.cookieNames.discountCoupon)
  const { offerType: couponType, code } = getCookie<{ offerType: string, code: string }>(constants.cookieNames.bindedCoupon) || {}

  const initCouponCode = paymentData && paymentData.couponCode
  const couponCode = code || initCouponCode || discountCoupon

  const {
    paymentType, subscriptionOrderNumber, subscriptionPlan,
    actualPrice, totalAmount, subscriptionPrice, subscriptionPriceWithDiscount,
  } = paymentData || {}

  // TODO remove subscriptionPrice and actual price, when we complete new paymentPage
  const totalAmountDollars = (
    parseFloat(actualPrice)
    || totalAmount / 100
    || parseFloat(subscriptionPriceWithDiscount)
    || subscriptionPrice
    || 0
  )

  const hasEcommerce = (
    paymentType === constants.paymentTypes.ecommerce
    || paymentType === constants.paymentTypes.subscriptionAndEcommerce
  )

  const handleDataLayerEvents = useCallback(() => {
    const isGift = paymentType === constants.paymentTypes.giftSubscription

    if (
      paymentType === constants.paymentTypes.coupon
      || paymentType === constants.paymentTypes.subscription
      || paymentType === constants.paymentTypes.subscriptionAndEcommerce
      || isGift
    ) {
      dataLayer.track('subscription thank you page', {
        subscription: {
          id: subscriptionOrderNumber,
          chargeAmount: totalAmountDollars,
          coupon: couponCode,
          plan: subscriptionPlan,
          couponType,
          isGift,
        },
      })
    }

    if (paymentType === constants.paymentTypes.giftCard) {
      dataLayer.track('gift card thank you page', {
        total: totalAmountDollars,
      })
    }

    if (hasEcommerce) {
      dataLayer.track('ecommerce thank you page', {
        total: totalAmountDollars,
      })
    }
  }, [ paymentData ])

  useEffect(() => {
    if (!paymentData) {
      if (redirectLink) {
        actions.models.router.replace(redirectLink)
      }
    }
    else {
      console.log('Analytics data:', paymentData)

      handleDataLayerEvents()

      // Cookies must be removed only after analytics events submitting
      cookiesToDelete.forEach((name) => removeCookie(name))
    }

    return () => actions.models.purchaseData.resetPaymentData()
  }, [ redirectLink, paymentData ])
}


export default usePurchaseAnalytics
